import { Check, ChevronDown } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import React, { useCallback, useRef, useState } from 'react';

import { Button, Icon } from '@column/column-ui-kit';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverAnchor,
  PopoverContent,
  StatusIndicator,
  cn,
} from '@column/ui';

import { ROUTE as APP_ROUTE } from '~/app/routes';
import { useNavigate } from '~/lib/navigation';
import { ROUTE as PUBLIC_ROUTE } from '~/public/routes';
import { useSessionStore } from '~/stores/Session';

export const ModeSelector: React.FC = () => {
  const { isSandbox, setSandbox, currentPlatform } = useSessionStore();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showLivePrompt, setShowLivePrompt] = useState(false);

  const [labelWidth, setLabelWidth] = useState<number>();
  const observerRef = useRef<ResizeObserver>();
  const sizerRef = useCallback((el: HTMLSpanElement | null) => {
    observerRef.current?.disconnect();
    if (!el) return;
    const update = () => setLabelWidth(Math.ceil(el.getBoundingClientRect().width));
    update();
    observerRef.current = new ResizeObserver(update);
    observerRef.current.observe(el);
  }, []);

  if (!currentPlatform) return null;

  const isLiveEnabled = currentPlatform.isLiveEnabled;
  const isLive = !isSandbox;
  const onboardingEnabled = currentPlatform?.metadata?.defaultDependencyGraphInstanceId;

  const handleModeChange = useCallback(
    (mode: 'live' | 'sandbox') => {
      setSandbox(mode === 'sandbox');
      setIsOpen(false);
    },
    [setSandbox]
  );

  const handleLiveClick = useCallback(() => {
    if (isLiveEnabled) {
      handleModeChange('live');
    } else {
      setIsOpen(false);
      setShowLivePrompt(true);
    }
  }, [isLiveEnabled, handleModeChange]);

  const triggerBaseClassName =
    'flex h-9 cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 text-sm outline-none transition-[background-color,border-color,color] duration-200 ease-out';
  const triggerVariantClassName = isLive
    ? 'border-gray-200 bg-transparent hover:bg-gray-50 hover:border-gray-300 data-[state=open]:bg-gray-50 dark:border-white/10 dark:hover:bg-white/[0.06] dark:hover:border-white/[0.15] dark:data-[state=open]:bg-white/[0.06]'
    : 'border-orange-700/10 bg-orange-200/50 text-orange-600/80 hover:bg-orange-200/70 data-[state=open]:bg-orange-200/70 [&_[data-slot=dropdown-chevron]]:text-orange-600/80 hover:[&_[data-slot=dropdown-chevron]]:text-orange-600/80 dark:border-orange-400/20 dark:bg-orange-500/15 dark:text-orange-300 dark:hover:bg-orange-500/25 dark:data-[state=open]:bg-orange-500/25 dark:[&_[data-slot=dropdown-chevron]]:text-orange-300 dark:hover:[&_[data-slot=dropdown-chevron]]:text-orange-300';

  const isTriggerOpen = isOpen || showLivePrompt;
  const triggerContent = (
    <>
      {isLiveEnabled ? (
        <StatusIndicator mode={isLive ? 'live' : 'sandbox'} className="shrink-0" />
      ) : (
        <Icon.Lock className="shrink-0" style={{ ['--icon-size' as string]: '16px' }} />
      )}
      <span
        style={{ width: labelWidth }}
        className="relative inline-block overflow-hidden font-medium transition-[width] duration-200 ease-out"
      >
        <span ref={sizerRef} aria-hidden className="invisible absolute top-0 left-0 whitespace-nowrap">
          {isLive ? 'Live' : 'Sandbox'}
        </span>
        <AnimatePresence initial={false} mode="wait">
          <motion.span
            key={isLive ? 'live' : 'sandbox'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1, ease: 'easeOut' }}
            className="inline-block whitespace-nowrap"
          >
            {isLive ? 'Live' : 'Sandbox'}
          </motion.span>
        </AnimatePresence>
      </span>
      <ChevronDown
        data-slot="dropdown-chevron"
        className={cn('size-4 transition-transform duration-200 ease-out', isTriggerOpen && 'rotate-180')}
      />
    </>
  );

  return (
    <Popover open={showLivePrompt} onOpenChange={setShowLivePrompt} modal>
      <PopoverAnchor asChild>
        <div>
          {isLiveEnabled ? (
            <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
              <DropdownMenuTrigger asChild>
                <button type="button" className={`${triggerBaseClassName} ${triggerVariantClassName}`}>
                  {triggerContent}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="min-w-32 rounded-lg" align="end" sideOffset={4}>
                <DropdownMenuItem onSelect={() => handleModeChange('sandbox')} className="gap-2 p-2">
                  <StatusIndicator mode="sandbox" className="shrink-0" />
                  <span className="flex-1 font-medium">Sandbox</span>
                  <Check className={`ml-auto size-4 shrink-0 ${!isLive ? '' : 'invisible'}`} />
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    handleLiveClick();
                  }}
                  className="gap-2 p-2"
                >
                  <StatusIndicator mode="live" className="shrink-0" />
                  <span className="flex-1 font-medium">Live</span>
                  <Check className={`ml-auto size-4 shrink-0 ${isLive ? '' : 'invisible'}`} />
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              type="button"
              data-state={showLivePrompt ? 'open' : 'closed'}
              onClick={() => setShowLivePrompt((v) => !v)}
              className={`${triggerBaseClassName} ${triggerVariantClassName}`}
            >
              {triggerContent}
            </button>
          )}
        </div>
      </PopoverAnchor>
      <PopoverContent align="end" className="w-60 p-4">
        <h4 className="mb-1 text-sm font-medium">
          {onboardingEnabled ? 'Continue onboarding' : 'Start moving real money'}
        </h4>
        <p className="mb-3 text-xs leading-4 text-muted-foreground">
          {onboardingEnabled
            ? 'Please finish our onboarding process for us to review.'
            : 'Please contact us to start your onboarding process.'}
        </p>
        {onboardingEnabled ? (
          <Button
            variant="primary"
            size="small"
            onClick={() => {
              navigate(PUBLIC_ROUTE.REGISTER_BUSINESS_INFORMATION);
              setShowLivePrompt(false);
            }}
          >
            Continue
          </Button>
        ) : (
          <Button
            variant="primary"
            size="small"
            onClick={() => {
              navigate(APP_ROUTE.CONTACT_FORM);
              setShowLivePrompt(false);
            }}
          >
            Contact us
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
};
